import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Link2, Unlink } from "lucide-react";
import useFetch from "@/lib/useFetch";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Footer, SectionCard } from "@/components/common/Page";
import Loader from "@/components/common/Loader";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";

export default function Connections() {
    const { refresh } = useAuth();
    const { data, reload } = useFetch("/discord/user");
    const { data: mine } = useFetch("/me/communities");
    const [busy, setBusy] = useState(false);

    if (!data) return <Loader />;
    const d = data.discord;

    const connect = async () => {
        setBusy(true);
        try {
            const { data: login } = await api.get("/discord/login");
            if (login.demo_mode) {
                await api.post("/discord/link", {});
                await refresh(); reload();
                toast.success("Discord linked (demo)", { description: "Add real Discord keys to connect live accounts." });
            } else {
                window.location.href = login.url;
            }
        } catch (e) {
            toast.error("Connect failed", { description: e.response?.data?.detail || "" });
        } finally { setBusy(false); }
    };

    const disconnect = async () => {
        await api.post("/discord/unlink");
        await refresh(); reload();
        toast("Discord disconnected", { description: "Your Outpost profile is kept." });
    };

    const refreshRoles = async () => {
        setBusy(true);
        try {
            await api.post("/discord/refresh-roles");
            toast.success("Roles refreshed");
        } catch (e) {
            toast.error("Refresh failed", { description: e.response?.data?.detail || "" });
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="player-connections">
            <PageHeader title="Connections" subtitle="Link your Discord account to sync roles and communities." />
            <SectionCard testId="discord-connection-card">
                <div className="flex items-start gap-4">
                    <img src={d?.avatar_url || "https://api.dicebear.com/7.x/bottts/svg?seed=discord"} alt="discord"
                        className="h-16 w-16 rounded-lg border border-discord/40 bg-background" />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-heading text-xl font-bold text-foreground">
                                {d ? (d.global_name || d.username) : "No Discord Linked"}
                            </h3>
                            <StatusBadge status={d ? "connected" : "disconnected"} testId="connection-status-badge" />
                        </div>
                        {d ? (
                            <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                                <div>Username: <span className="text-foreground">@{d.username}</span></div>
                                <div>User ID: <span className="font-mono text-foreground">{d.id}</span></div>
                                <div>Guild Member: <span className="text-radioactive">{d.guild_member ? "Yes" : "No"}</span></div>
                                <div>Communities: <span className="text-foreground">{(mine || []).length}</span></div>
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground mt-2">
                                Connect Discord to automatically receive your community and platform roles.
                            </p>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-5">
                    {!d ? (
                        <Button data-testid="connect-discord-btn" disabled={busy} onClick={connect}
                            className="font-heading uppercase bg-discord text-white hover:bg-discord/90">
                            {busy ? <Loader2 className="animate-spin" size={15} /> : <><Link2 size={15} className="mr-1" /> Connect Discord</>}
                        </Button>
                    ) : (
                        <>
                            <Button data-testid="reconnect-discord-btn" disabled={busy} onClick={connect} variant="outline"
                                className="font-heading uppercase border-discord/40 text-discord hover:bg-discord/10">
                                <RefreshCw size={15} className="mr-1" /> Reconnect
                            </Button>
                            <Button data-testid="refresh-roles-btn" disabled={busy} onClick={refreshRoles} variant="outline"
                                className="font-heading uppercase border-hazard/40 text-hazard hover:bg-hazard/10">
                                {busy ? <Loader2 className="animate-spin" size={15} /> : <><RefreshCw size={15} className="mr-1" /> Refresh Roles</>}
                            </Button>
                            <Button data-testid="disconnect-discord-btn" onClick={disconnect} variant="outline"
                                className="font-heading uppercase border-danger/40 text-danger hover:bg-danger/10">
                                <Unlink size={15} className="mr-1" /> Disconnect
                            </Button>
                        </>
                    )}
                </div>
            </SectionCard>
            <Footer />
        </div>
    );
}
